import type { RecipeV2Doc } from '@ctcells/api'
import { useDeckDefinition } from '@ctcells/api'
import { t } from '@ctcells/i18n'

import { deckRacksOf, sameLabware } from '../monitoring/deckRacks.ts'
import {
  deckLayout,
  toDeck,
  type AreaBox,
  type PlacedLabware,
} from '../recipes/editor/deckModel.ts'
import { isNewFormat } from '../recipes/editor/editorModel.ts'

/*
 * 🔴 **서버 데크 정의의 `name` 글자를 읽는 판정이다**(`app/resources/admin/decks/*.json` 의 「칸 A1」 · 「셰이커 자리」).
 *    화면 언어와 상관없이 서버가 한글로 준다 — `t()` 로 옮기면 영어에서 판정이 깨진다. 서버가 칸의 종류를 따로 주면 사라질 값이다.
 */
const STANDARD_SLOT_HEAD = '칸'
const SERVER_AREA_TAIL = ' 자리'

/**
 * **염색기 데크 그림** — 레시피 「랩웨어」 표 위(eric 2026-10-20 「랩웨어 부분인데.. 이것도 그림으로 그려줘」).
 *
 * ```
 * ┌ A1 ┐┌ A2 ┐┌ 셰이커 ┐      ┌──────┐
 * │    ││    ││ 챔버    │      │폐기통│
 * ├ B1 ┤├ B2 ┤├ 냉장 ──┤      │      │
 * │Tube││Tube││Cold    │      └──────┘
 * ├ C1 ┤├ C2 ┤
 * │Tip ││Tip │
 * ```
 * 🔴 **자리 · 크기는 실물 정의에서만 온다** — 데크 정의(`/resources/admin/decks/<robot.deckId>.json`)의 칸 위치 · 크기와 문서의 랩웨어 정의
 *    (`labwareDefinitions` 의 외곽 · 웰). 셈은 백엔드 편집기를 옮긴 `deckLayout`(`recipes/editor/deckModel.ts`) 그대로다 — 데크를 못 받으면
 *    그림 없이 표만 선다(데크를 지어 그리지 않는다).
 * 🔴 색을 칠하지 않는다(콘솔 색 규칙) — 액체가 실린 웰은 회색 채움, 빈 웰은 테두리. 무엇이 실렸는지는 아래 「시약」 그림 · 표가 말한다.
 * Opentrons 좌표는 y 가 위로 커진다 — 그림은 뒤집어 앞(사람 쪽)이 아래로 선다.
 */
export function RecipeDeckFigure({ doc }: { doc: RecipeV2Doc }) {
  const editor = isNewFormat(doc) ? doc : undefined
  const deckId = editor?.robot?.deckId
  const deck = useDeckDefinition({ deckId })
  const layout = editor === undefined ? null : deckLayout(editor, deck.data)
  if (layout === null) return null
  const { minX, minY, maxX, maxY } = layout.bounds
  const flip = (y: number) => maxY + minY - y
  const boxes = layout.boxes.filter((box) => box.type !== 'fixedTrash')
  /*
   * 축 글자(위 열 숫자 · 왼쪽 줄 글자)가 그림 밖으로 잘리지 않게 그 자리만큼 viewBox 를 넓힌다(eric 2026-10-21 「그림에서 1,2 위쪽이 짤려」) —
   * 여백은 축 글자 크기(`AXIS_FONT`)에서 잰다. 데크 정의 여백(10)보다 축이 더 나가면 그만큼만 더
   */
  const axes = axisAnchor(layout.boxes, flip)
  const vbX = axes === null ? minX : Math.min(minX, axes.left - AXIS_GAP - AXIS_FONT)
  const vbY = axes === null ? minY : Math.min(minY, axes.top - AXIS_GAP - AXIS_FONT)
  return (
    <figure className="rtpl__deck" data-part="deck">
      <svg
        aria-label={layout.placed.map((q) => labwareName(q.labwareId)).join(' · ')}
        className="fig rtpl__deckfig"
        role="img"
        viewBox={`${f(vbX)} ${f(vbY)} ${f(maxX - vbX)} ${f(maxY - vbY)}`}
      >
        {boxes.map((box) => (
          <g data-area={box.id} key={box.id}>
            <rect
              className="rtpl__deckarea"
              height={f(box.h)}
              rx={4}
              width={f(box.w)}
              x={f(box.x)}
              y={f(flip(box.y + box.h))}
            />
            {/* 빈 표준 칸(「칸 A1」 …)은 이름 대신 데크 축(왼쪽 줄 글자 · 위 열 숫자)이 말한다 — 이름은 셰이커 · 냉장 · 폐기통 자리만 */}
            {isGridSlot(box) && box.name.startsWith(STANDARD_SLOT_HEAD) ? null : (
              <text className="rtpl__deckslot" x={f(box.x + 4)} y={f(flip(box.y + box.h) + 11)}>
                {areaName(box)}
              </text>
            )}
          </g>
        ))}
        <Slider boxes={layout.boxes} flip={flip} />
        {layout.placed.map((q) => (
          <Labware fills={layout.fills} flip={flip} key={q.labwareId} placed={q} />
        ))}
        <DeckAxes boxes={layout.boxes} flip={flip} />
      </svg>
    </figure>
  )
}

/** 놓인 랩웨어 한 장 — 외곽 · 웰(채움 = 액체 실림) · 트레이 칸 · 이름 */
function Labware({
  placed: q,
  fills,
  flip,
}: {
  placed: PlacedLabware
  fills: Readonly<Record<string, string>>
  flip: (y: number) => number
}) {
  const at = (x: number, y: number): [number, number] => {
    const [dx, dy] = toDeck(q, x, y)
    return [dx, flip(dy)]
  }
  const name = labwareName(q.labwareId)
  return (
    <g className="rtpl__decklw" data-labware={q.labwareId}>
      <rect
        className="rtpl__decklw-body"
        height={f(q.lh)}
        rx={3}
        width={f(q.lw)}
        x={f(q.ox)}
        y={f(flip(q.oy + q.lh))}
      />
      {q.shapes.map((s, i) => {
        if (s.kind === 'handle') return null
        const [cx, cy] = at(s.x, s.y)
        if (s.kind === 'pocket') {
          const [w, h] = q.rotated ? [s.h, s.w] : [s.w, s.h]
          return (
            <rect
              className={
                s.balance ? 'rtpl__deckpocket rtpl__deckpocket--balance' : 'rtpl__deckpocket'
              }
              height={f(h)}
              key={`p${String(i)}`}
              rx={1.5}
              width={f(w)}
              x={f(cx - w / 2)}
              y={f(cy - h / 2)}
            />
          )
        }
        const filled = fills[`${q.labwareId}|${s.name}`] !== undefined
        const cls = filled ? 'rtpl__deckwell rtpl__deckwell--filled' : 'rtpl__deckwell'
        if (s.shape === 'circular')
          return <circle className={cls} cx={f(cx)} cy={f(cy)} key={s.name} r={f(s.diameter / 2)} />
        const [w, h] = q.rotated ? [s.h, s.w] : [s.w, s.h]
        return (
          <rect
            className={cls}
            height={f(h)}
            key={s.name}
            rx={1}
            width={f(w)}
            x={f(cx - w / 2)}
            y={f(cy - h / 2)}
          />
        )
      })}
      <text className="rtpl__deckname" x={f(q.ox + q.lw / 2)} y={f(flip(q.oy) - 5)}>
        {name}
      </text>
    </g>
  )
}

/**
 * **슬라이더** — 데크 3열(셰이커 자리 `A3`)은 트레이가 앞뒤로 오가는 슬라이더다(eric 2026-10-20 「3 으로 되어 잇는 것에는.. 이게 슬라이더야..
 * 슬라이더를 그려줘.. 3번째 열에」). 3열 칸 폭 안에 레일 두 줄을 열 위끝부터 데크 칸 아래끝까지 깐다 — 랩웨어 밑에 그려 트레이 · 냉장 블록이 위에 선다.
 * 자리는 데크 정의 칸 좌표에서만 읽는다(3열 칸이 없으면 안 그린다).
 */
function Slider({ boxes, flip }: { boxes: readonly AreaBox[]; flip: (y: number) => number }) {
  const grid = boxes.filter(isGridSlot)
  const column = grid.filter((b) => b.id.endsWith('3'))
  if (column.length === 0) return null
  const x0 = Math.min(...column.map((b) => b.x))
  const x1 = Math.max(...column.map((b) => b.x + b.w))
  const top = Math.min(...column.map((b) => flip(b.y + b.h)))
  const bottom = Math.max(...grid.map((b) => flip(b.y)))
  const rail = 3
  const inset = 10
  return (
    <g aria-hidden="true" className="rtpl__deckslider">
      {[x0 + inset, x1 - inset - rail].map((x) => (
        <rect
          className="rtpl__deckrail"
          height={f(bottom - top)}
          key={x}
          rx={1}
          width={rail}
          x={f(x)}
          y={f(top)}
        />
      ))}
    </g>
  )
}

/** 데크 칸 격자 이름(`A1` · `B2` · `A3` …)인가 — 데크 정의 칸 id 가 줄 글자 + 열 숫자 */
function isGridSlot(box: AreaBox): boolean {
  return box.type === 'slot' && /^[A-D]\d$/u.test(box.id)
}

/**
 * **데크 축** — 데크 칸의 줄 글자(A · B · C)는 왼쪽, 열 숫자(1 · 2 · 3)는 위(eric 2026-10-20 「deck a,b,c 1,2 이거 해달라고」 — 소모품 랙 그림의
 * 축과 같은 결). 자리는 데크 정의 칸 좌표에서 읽는다 — 같은 글자 칸들의 세로 가운데 · 같은 숫자 칸들의 가로 가운데.
 */
function DeckAxes({ boxes, flip }: { boxes: readonly AreaBox[]; flip: (y: number) => number }) {
  const grid = boxes.filter(isGridSlot)
  const anchor = axisAnchor(boxes, flip)
  if (anchor === null) return null
  const { left, top } = anchor
  /* 줄 · 열 가운데는 표준 칸(「칸 …」)으로 잰다 — 셰이커 · 냉장 자리는 키가 달라 줄 가운데를 끌어내린다. 표준 칸이 없는 열(3)만 그 자리로 */
  const standard = grid.filter((b) => b.name.startsWith(STANDARD_SLOT_HEAD))
  const centers = (pick: (b: AreaBox) => string, axis: 'x' | 'y') => {
    const groups = new Map<string, number[]>()
    const source = (label: string) => (standard.some((b) => pick(b) === label) ? standard : grid)
    for (const b of grid.filter((x) => source(pick(x)).includes(x))) {
      const c = axis === 'x' ? b.x + b.w / 2 : flip(b.y + b.h / 2)
      groups.set(pick(b), [...(groups.get(pick(b)) ?? []), c])
    }
    return [...groups].map(([label, list]) => ({
      label,
      at: list.reduce((sum, v) => sum + v, 0) / list.length,
    }))
  }
  return (
    <g aria-hidden="true" className="rtpl__deckaxes">
      {centers((b) => b.id.slice(0, 1), 'y').map((row) => (
        <text
          className="rtpl__deckaxis"
          dominantBaseline="central"
          fontSize={AXIS_FONT}
          key={`r${row.label}`}
          textAnchor="end"
          x={f(left - AXIS_GAP)}
          y={f(row.at)}
        >
          {row.label}
        </text>
      ))}
      {/* 열 숫자는 표준 칸이 있는 열만(1 · 2) — 3열은 칸이 아니라 슬라이더 · 냉장 자리다(eric 2026-10-20 「A3은 없어..」) · 그 이름이 자리에 선다 */}
      {centers((b) => b.id.slice(1), 'x')
        .filter((col) => standard.some((b) => b.id.slice(1) === col.label))
        .map((col) => (
          <text
            className="rtpl__deckaxis"
            fontSize={AXIS_FONT}
            key={`c${col.label}`}
            textAnchor="middle"
            x={f(col.at)}
            y={f(top - AXIS_GAP)}
          >
            {col.label}
          </text>
        ))}
    </g>
  )
}

/**
 * 데크 축 글자 크기 · 칸과의 간격(svg 단위 = 데크 mm). 글자 크기를 CSS 가 아니라 여기 둔다 — viewBox 여백을 같은 값에서 재야 글자가 안 잘린다
 * (CSS 로 키우면 여백이 따라오지 않는다 · 2026-10-21 실제로 잘렸다)
 */
const AXIS_FONT = 12
const AXIS_GAP = 4

/** 축 글자의 기준 — 데크 칸 격자의 왼쪽 끝 · 위 끝(그림 좌표). 격자 칸이 없으면 축도 없다 */
function axisAnchor(
  boxes: readonly AreaBox[],
  flip: (y: number) => number,
): { left: number; top: number } | null {
  const grid = boxes.filter(isGridSlot)
  if (grid.length === 0) return null
  return {
    left: Math.min(...grid.map((b) => b.x)),
    top: Math.min(...grid.map((b) => flip(b.y + b.h))),
  }
}

/** 랩웨어 이름 — 랙은 모니터링 소모품과 같은 이름(Tip rack 1 · Tube rack 1 · Cold rack), 트레이 · 폐기통은 짧은 한글, 모르면 id */
function labwareName(labwareId: string): string {
  const rack = deckRacksOf('stain').find((entry) => sameLabware(entry.labwareId, labwareId))
  if (rack !== undefined) return rack.aria()
  if (labwareId.startsWith('hypicc.tray')) return t('enum.labwareType.hypicc_chamber')
  if (labwareId.startsWith('staining.waste')) return t('monitoring.waste')
  return labwareId
}

/** 자리 이름 — 표준 칸(데크 정의 이름 「칸 …」)은 칸 이름(C1 …), 그 밖은 데크 정의 이름의 「… 자리」 앞(셰이커 · 냉장 · 폐기통) */
function areaName(box: AreaBox): string {
  if (box.name.startsWith(STANDARD_SLOT_HEAD)) return box.id
  /* 셰이커 자리(3열 맨 위)는 슬라이더 위다 — 「슬라이더」로 적는다(eric 2026-10-20 「이게 슬라이더야」) */
  if (box.id === 'A3') return t('metric.slider')
  const cut = box.name.indexOf(SERVER_AREA_TAIL)
  return cut > 0 ? box.name.slice(0, cut) : box.name
}

/** 소수 둘째 자리까지 — SVG 속성 글자를 짧게 */
function f(n: number): number {
  return Math.round(n * 100) / 100
}
